import React, { FC } from 'react';

import LabelledDropdown from '~components/atoms/LabelledDropdown';
import LabelledInput from '~components/atoms/LabelledInput';
import ActionButton from '~components/atoms/ActionButton';

import { ErrorMessages } from '~utils/constants';
import UtilFunctions from '~utils/functions';
import { TDropdownOption } from '~types/components/labelledDropdown';

import * as Styles from './_searchAndFilterBar.module.scss';
import LabelledDatepicker from '~components/atoms/LabelledDatepicker';

export type TSearchAndFilterBarProps = {
    fields: {
        label: string;
        type: 'input' | 'dropdown' | 'datepicker';
        dropdownParams?: {
            options: TDropdownOption[];
            value: string;
            setValue: (event: React.ChangeEvent<HTMLInputElement>) => void;
            optionSelected: boolean;
            noOptionsMessage?: string;
            className?: string;
            disabled?: boolean;
            inputTestId?: string;
            optionTestId?: string;
        };
        inputParams?: {
            value: string;
            setValue: (event: React.ChangeEvent<HTMLInputElement>) => void;
            className?: string;
            onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
            testId?: string;
        };
        datepickerParams?: {
            date: string;
            setDate: (date: string) => void;
            startDate?: string;
            endDate?: string;
            selectsStart?: boolean;
            selectsEnd?: boolean;
            minDate?: string;
            maxDate?: string;
            disabled?: boolean;
        };
    }[];
    searchOnClick: () => void;
    cancelOnClick: () => void;
    containerClassName?: string;
    gridColumns?: number;
}

const SearchAndFilterBar: FC<TSearchAndFilterBarProps> = ({
    fields,
    searchOnClick,
    cancelOnClick,
    containerClassName,
    gridColumns,
}) => {
    const containerStyle = UtilFunctions.Styles.concatClassNames([
        Styles.container,
        containerClassName,
    ]);

    if (!fields || fields?.length <= 0) return null;

    const renderFields = () => {
        return fields.map(field => {
            const { label, type } = field;

            if (field?.dropdownParams && type === 'dropdown') {
                const { dropdownParams: {
                    options,
                    value,
                    setValue,
                    optionSelected,
                    noOptionsMessage,
                    className,
                    disabled,
                    inputTestId,
                    optionTestId,
                } } = field;

                return (
                    <LabelledDropdown
                        isFilter
                        key={label}
                        label={label}
                        options={options || []}
                        error={!options ? ErrorMessages.MissingRequiredPropertiesError : ''}
                        value={value}
                        onChange={setValue}
                        optionSelected={optionSelected}
                        noOptionsMessage={noOptionsMessage}
                        className={className}
                        disabled={disabled}
                        inputTestId={inputTestId}
                        optionTestId={optionTestId}
                    />
                );
            }

            if (field?.datepickerParams && type === 'datepicker') {
                const { datepickerParams: {
                    date,
                    setDate,
                    startDate,
                    endDate,
                    selectsStart,
                    selectsEnd,
                    minDate,
                    maxDate,
                    disabled,
                } } = field;
                if (!setDate) return null;

                return (
                    <LabelledDatepicker
                        isFilter
                        key={label}
                        label={label}
                        selectedDate={date}
                        setSelectedDate={setDate}
                        minDate={minDate}
                        maxDate={maxDate}
                        startDate={startDate}
                        endDate={endDate}
                        selectsStart={selectsStart}
                        selectsEnd={selectsEnd}
                        disabled={disabled}
                    />
                );
            }

            if (field?.inputParams && type === 'input') {
                const { inputParams: {
                    value,
                    setValue,
                    onKeyDown,
                    testId,
                    className,
                } } = field;

                if (!setValue) return null;

                return (
                    <LabelledInput
                        isFilter
                        key={label}
                        label={label}
                        value={value}
                        onChange={setValue}
                        onKeyDown={onKeyDown}
                        className={className}
                        data-testid={testId}
                    />
                );
            }

            return null;
        });
    };

    return (
        <div className={containerStyle}
        >
            <div className={Styles['fields-container']}
                style={{ display: 'grid', gridTemplateColumns: `repeat(${gridColumns || 3}, minmax(200px, 250px))` }}
            >
                {renderFields()}
            </div>

            <ActionButton
                testId='filter-clear-button'
                label='Clear'
                onClick={cancelOnClick}
                className={`${Styles['action-button']} ${Styles['is-red']}`}
            />

            <ActionButton
                testId='filter-search-button'
                label='Search'
                onClick={searchOnClick}
                className={Styles['action-button']}
            />
        </div>
    );
};

export default SearchAndFilterBar;
